/**
 * HTML Report Generator
 * Creates styled HTML reports from JSON data
 */

import type {
  StudySummaryJSON,
  StudyMetricDetailJSON,
  ParticipantSummaryJSON,
  ParticipantMissingDataJSON,
  ParticipantMetricsStatsJSON,
} from "../services/dataCoverageReportExportService";

const HTML_STYLES = `
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    line-height: 1.6;
    color: #333;
    background: #f5f5f5;
  }
  .container { max-width: 1200px; margin: 0 auto; padding: 20px; background: white; }
  .header {
    border-bottom: 3px solid #8C1515;
    margin-bottom: 30px;
    padding-bottom: 20px;
  }
  h1 { font-size: 28px; color: #8C1515; margin-bottom: 10px; }
  h2 { font-size: 20px; color: #333; margin-top: 30px; margin-bottom: 15px; border-bottom: 2px solid #e0e0e0; padding-bottom: 8px; }
  h3 { font-size: 16px; color: #555; margin-top: 20px; margin-bottom: 10px; }
  .metadata {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;
    font-size: 14px;
  }
  .metadata-item {
    padding: 12px;
    background: #f9f9f9;
    border-left: 4px solid #8C1515;
  }
  .metadata-label { font-weight: 600; color: #666; display: block; }
  .metadata-value { color: #333; margin-top: 4px; }
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
    margin-bottom: 30px;
  }
  .stat-card {
    padding: 20px;
    background: linear-gradient(135deg, #f5f5f5 0%, #efefef 100%);
    border-radius: 8px;
    border-left: 4px solid #8C1515;
  }
  .stat-label { font-size: 12px; color: #666; font-weight: 600; text-transform: uppercase; }
  .stat-value { font-size: 28px; color: #8C1515; font-weight: 700; margin-top: 8px; }
  .stat-unit { font-size: 14px; color: #999; }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
  thead {
    background: #8C1515;
    color: white;
  }
  th {
    padding: 12px;
    text-align: left;
    font-weight: 600;
  }
  td {
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
  }
  tbody tr:hover { background: #f9f9f9; }
  .log-entry {
    padding: 10px;
    margin: 8px 0;
    border-radius: 4px;
    font-size: 13px;
    font-family: 'Courier New', monospace;
  }
  .log-info { background: #e3f2fd; border-left: 4px solid #2196F3; }
  .log-warning { background: #fff3e0; border-left: 4px solid #ff9800; }
  .log-error { background: #ffebee; border-left: 4px solid #f44336; }
  .range-item {
    display: grid;
    grid-template-columns: 1fr 1fr 100px;
    gap: 15px;
    align-items: center;
    padding: 10px;
    background: #f9f9f9;
    margin: 5px 0;
    border-radius: 4px;
    font-size: 13px;
  }
  .metric-section {
    background: #fafafa;
    padding: 20px;
    margin: 15px 0;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
  }
  .footer {
    margin-top: 50px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
    text-align: center;
    color: #999;
    font-size: 12px;
  }
  .heatmap-image {
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fff;
    margin-top: 12px;
  }
  @media print {
    body { background: white; }
    .container { padding: 0; box-shadow: none; }
    table { page-break-inside: avoid; }
    h2 { page-break-after: avoid; }
    .metric-section { page-break-inside: avoid; }
  }
</style>
`;

class HtmlReportGenerator {
  /**
   * Generate Study Level Report HTML
   */
  generateStudyReport(
    summary: StudySummaryJSON,
    metricDetail?: StudyMetricDetailJSON,
    heatmapImageDataUrl?: string,
  ): string {
    const metricsList = summary.metricsOverview
      .map(
        (m) =>
          `
      <tr>
        <td>${this.escapeHtml(m.metricName)}</td>
        <td style="text-align: right;">${m.avgCoveragePct.toFixed(1)}%</td>
        <td style="text-align: right;">${m.minCoveragePct.toFixed(1)}%</td>
        <td style="text-align: right;">${m.maxCoveragePct.toFixed(1)}%</td>
      </tr>
    `,
      )
      .join("");

    const logsHtml = summary.summaryStats.logs
      .map(
        (log) =>
          `<div class="log-entry log-${log.level}"><strong>${log.timestamp}</strong> [${log.level.toUpperCase()}] ${this.escapeHtml(log.message)}</div>`,
      )
      .join("");

    const metricsDetailHtml =
      metricDetail && metricDetail.metricDetails.length > 0
        ? metricDetail.metricDetails
            .map((metric) => this.generateMetricDetailSection(metric))
            .join("")
        : "";

    const heatmapSectionHtml = heatmapImageDataUrl
      ? `<h2>Coverage Heatmap</h2><img class="heatmap-image" src="${heatmapImageDataUrl}" alt="Study coverage heatmap" />`
      : "";

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Study Level Report - ${this.escapeHtml(summary.metadata.studyName)}</title>
  ${HTML_STYLES}
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>📊 Study Level Data Coverage Report</h1>
      <p style="color: #666; margin-top: 10px;">${this.escapeHtml(summary.metadata.studyName)}</p>
    </div>

    <div class="metadata">
      <div class="metadata-item">
        <span class="metadata-label">Study ID</span>
        <span class="metadata-value">${this.escapeHtml(summary.metadata.studyId)}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">Exported</span>
        <span class="metadata-value">${new Date(summary.metadata.exportedAt).toLocaleString()}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">Date Range</span>
        <span class="metadata-value">${summary.summaryStats.dateRange.fromDate} to ${summary.summaryStats.dateRange.endDate}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">Total Days</span>
        <span class="metadata-value">${summary.summaryStats.dateRange.totalDays} days</span>
      </div>
    </div>

    <h2>Summary Statistics</h2>
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-label">Participants Analyzed</div>
        <div class="stat-value">${summary.summaryStats.participantsAnalyzed}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Average Coverage</div>
        <div class="stat-value">${summary.summaryStats.avgCoveragePct.toFixed(1)}<span class="stat-unit">%</span></div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Metrics Available</div>
        <div class="stat-value">${summary.metricsOverview.length}</div>
      </div>
    </div>

    <h2>Metrics Overview</h2>
    <table>
      <thead>
        <tr>
          <th>Metric Name</th>
          <th style="text-align: right;">Avg Coverage</th>
          <th style="text-align: right;">Min Coverage</th>
          <th style="text-align: right;">Max Coverage</th>
        </tr>
      </thead>
      <tbody>
        ${metricsList}
      </tbody>
    </table>

    ${logsHtml ? `<h2>Data Fetch Logs</h2><div>${logsHtml}</div>` : ""}

    ${heatmapSectionHtml}

    ${metricsDetailHtml}

    <div class="footer">
      <p>Report generated by StudySync Data Coverage Module</p>
      <p>Version: ${summary.metadata.jsonVersion}</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  /**
   * Generate Participant Level Report HTML
   */
  generateParticipantReport(
    summary: ParticipantSummaryJSON,
    missingData?: ParticipantMissingDataJSON,
    metricsStats?: ParticipantMetricsStatsJSON,
    heatmapImageDataUrl?: string,
  ): string {
    const logsHtml = summary.summaryStats.logs
      .map(
        (log) =>
          `<div class="log-entry log-${log.level}"><strong>${log.timestamp}</strong> [${log.level.toUpperCase()}] ${this.escapeHtml(log.message)}</div>`,
      )
      .join("");

    const missingDataHtml = missingData
      ? this.generateMissingDataSection(missingData)
      : "";

    const metricsStatsHtml = metricsStats
      ? this.generateMetricsStatsSection(metricsStats)
      : "";

    const metricCoverageRows = summary.metricCoverage
      .map(
        (metric) => `
      <tr>
        <td>${this.escapeHtml(metric.metricName)}</td>
        <td style="text-align: right;">${metric.coveragePct.toFixed(1)}%</td>
        <td style="text-align: right;">${metric.daysWithData}</td>
        <td style="text-align: right;">${metric.missingDataCount}</td>
      </tr>
    `,
      )
      .join("");

    const metricCoverageHtml = summary.metricCoverage.length
      ? `<h2>Metric Coverage Detail</h2>
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th style="text-align: right;">Coverage</th>
            <th style="text-align: right;">Days w/ Data</th>
            <th style="text-align: right;">Missing Days</th>
          </tr>
        </thead>
        <tbody>
          ${metricCoverageRows}
        </tbody>
      </table>`
      : "";

    const heatmapSectionHtml = heatmapImageDataUrl
      ? `<h2>Coverage Heatmap</h2><img class="heatmap-image" src="${heatmapImageDataUrl}" alt="Participant coverage heatmap" />`
      : "";

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Participant Report - ${this.escapeHtml(summary.metadata.participantName)}</title>
  ${HTML_STYLES}
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>👤 Participant Level Data Coverage Report</h1>
      <p style="color: #666; margin-top: 10px;">${this.escapeHtml(summary.metadata.participantName)}</p>
    </div>

    <div class="metadata">
      <div class="metadata-item">
        <span class="metadata-label">Participant ID</span>
        <span class="metadata-value">${this.escapeHtml(summary.metadata.participantId)}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">Device Type</span>
        <span class="metadata-value">${summary.summaryStats.deviceType}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">Date Range</span>
        <span class="metadata-value">${summary.summaryStats.dateRange.fromDate} to ${summary.summaryStats.dateRange.endDate}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">Exported</span>
        <span class="metadata-value">${new Date(summary.metadata.exportedAt).toLocaleString()}</span>
      </div>
    </div>

    <h2>Summary Statistics</h2>
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-label">Metrics with Data</div>
        <div class="stat-value">${summary.summaryStats.metricsWithData} <span class="stat-unit">/ ${summary.summaryStats.totalMetrics}</span></div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Average Coverage</div>
        <div class="stat-value">${summary.summaryStats.avgCoveragePct.toFixed(1)}<span class="stat-unit">%</span></div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Date Range</div>
        <div class="stat-value">${summary.summaryStats.dateRange.totalDays}<span class="stat-unit"> days</span></div>
      </div>
    </div>

    ${logsHtml ? `<h2>Data Fetch Logs</h2><div>${logsHtml}</div>` : ""}

    ${metricCoverageHtml}

    ${heatmapSectionHtml}

    ${missingDataHtml}

    ${metricsStatsHtml}

    <div class="footer">
      <p>Report generated by StudySync Data Coverage Module</p>
      <p>Version: ${summary.metadata.jsonVersion}</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  /**
   * Generate metric detail section for study reports
   */
  private generateMetricDetailSection(metric: any): string {
    const cohortBars = metric.coverageDistributionByCohort
      .map(
        (cohort: any) =>
          `<div style="display: flex; align-items: center; gap: 16px; padding: 12px; border-radius: 4px; background: #fff; border: 1px solid #e8e8e8; margin: 8px 0;">
            <div style="display: flex; align-items: center; gap: 8px; min-width: 120px;">
              <div style="width: 12px; height: 12px; border-radius: 2px; background: ${this.getCohortColor(cohort.range)};"></div>
              <span style="font-size: 13px; font-weight: 500; color: #333;">${cohort.range}</span>
            </div>
            <div style="flex: 1; text-align: center;">
              <div style="font-size: 20px; font-weight: bold; color: ${this.getCohortColor(cohort.range)};">${cohort.percentage.toFixed(1)}%</div>
            </div>
            <div style="text-align: right; min-width: 100px; font-size: 12px; color: #666;">
              <div>${cohort.participantCount} participant${cohort.participantCount !== 1 ? "s" : ""}</div>
            </div>
          </div>`,
      )
      .join("");

    const lowCoverageRows = metric.lowCoverageParticipants
      .slice(0, 10)
      .map(
        (p: any) =>
          `<tr>
        <td>${this.escapeHtml(p.participantName)}</td>
        <td style="text-align: right;">${p.coveragePct.toFixed(1)}%</td>
        <td style="text-align: right;">${p.daysWithData}</td>
        <td style="text-align: right;">${p.meanValue ? p.meanValue.toFixed(2) : "-"}</td>
      </tr>`,
      )
      .join("");

    return `
      <div class="metric-section">
        <h3>${this.escapeHtml(metric.metricName)}</h3>
        <div class="stats-grid" style="grid-template-columns: repeat(3, 1fr);">
          <div class="stat-card">
            <div class="stat-label">Avg Coverage</div>
            <div class="stat-value">${metric.avgCoveragePct.toFixed(1)}<span class="stat-unit">%</span></div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Participants</div>
            <div class="stat-value">${metric.participantCount}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Mean Value</div>
            <div class="stat-value">${metric.statistics.meanValue ? metric.statistics.meanValue.toFixed(2) : "-"}</div>
          </div>
        </div>

        <h4 style="margin-top: 15px; margin-bottom: 12px; color: #444;">Coverage Distribution</h4>
        <div style="background: #fafafa; padding: 12px; border-radius: 4px;">
          ${cohortBars}
        </div>

        ${
          lowCoverageRows
            ? `<h4 style="margin-top: 15px; margin-bottom: 10px;">Low Coverage Participants</h4>
        <table>
          <thead>
            <tr>
              <th>Participant Name</th>
              <th style="text-align: right;">Coverage</th>
              <th style="text-align: right;">Days w/ Data</th>
              <th style="text-align: right;">Mean Value</th>
            </tr>
          </thead>
          <tbody>${lowCoverageRows}</tbody>
        </table>`
            : ""
        }
      </div>
    `;
  }

  /**
   * Generate missing data section
   */
  private generateMissingDataSection(data: ParticipantMissingDataJSON): string {
    const rangeItems = data.missingDataAnalysis.missingDateRanges
      .slice(0, 20)
      .map(
        (r) => `
      <div class="range-item">
        <div><strong>${r.startDate}</strong></div>
        <div><strong>to</strong> ${r.endDate}</div>
        <div style="text-align: right;">${r.dayCount} days</div>
      </div>
    `,
      )
      .join("");

    return `
      <h2>Missing Data Analysis</h2>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Coverage</div>
          <div class="stat-value">${data.missingDataAnalysis.coveragePct.toFixed(1)}<span class="stat-unit">%</span></div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Days w/ Coverage</div>
          <div class="stat-value">${data.missingDataAnalysis.daysWithCoverage}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Days w/o Coverage</div>
          <div class="stat-value">${data.missingDataAnalysis.totalMissingDays}</div>
        </div>
      </div>

      ${data.missingDataAnalysis.missingDateRanges.length > 0 ? `<h3>Missing Date Ranges</h3>${rangeItems}` : "<p style='color: #999;'>No missing date ranges detected.</p>"}
    `;
  }

  /**
   * Generate metrics statistics section
   */
  private generateMetricsStatsSection(
    data: ParticipantMetricsStatsJSON,
  ): string {
    const metricsHtml = data.metricsStatistics
      .map(
        (metric) => `
      <div class="metric-section">
        <h3>${this.escapeHtml(metric.metricName)}</h3>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-label">Coverage</div>
            <div class="stat-value">${metric.statistics.coveragePct.toFixed(1)}<span class="stat-unit">%</span></div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Days w/ Data</div>
            <div class="stat-value">${metric.statistics.daysWithData}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Mean Value</div>
            <div class="stat-value">${metric.statistics.meanValue !== null ? metric.statistics.meanValue.toFixed(2) : "-"}</div>
          </div>
        </div>
        <table style="font-size: 13px;">
          <thead>
            <tr>
              <th>Period</th>
              <th style="text-align: right;">Days</th>
              <th style="text-align: right;">Coverage</th>
              <th style="text-align: right;">Value</th>
            </tr>
          </thead>
          <tbody>
            ${metric.aggregationPeriods
              .slice(0, 30)
              .map(
                (p) => `
              <tr>
                <td>${this.escapeHtml(p.period)}</td>
                <td style="text-align: right;">${p.daysWithData}</td>
                <td style="text-align: right;">${p.coveragePct?.toFixed(1) || "-"}%</td>
                <td style="text-align: right;">${p.value !== null ? p.value.toFixed(2) : "-"}</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `,
      )
      .join("");

    return `<h2>Metrics Statistics</h2>${metricsHtml}`;
  }

  /**
   * Helper to get cohort color
   */
  private getCohortColor(range: string): string {
    if (range.includes("0-25")) return "#d32f2f";
    if (range.includes("25-50")) return "#ff9800";
    if (range.includes("50-75")) return "#fbc02d";
    return "#4caf50";
  }

  /**
   * Helper to escape HTML
   */
  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }
}

export const htmlReportGenerator = new HtmlReportGenerator();
